import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, message, notification } from 'antd';
import type { AxiosError } from 'axios';
import { createElement } from 'react';
import api from 'constants/api';
import {
  JourneyKind,
  Action,
  AuditLog,
  GeolocationType,
  Issue,
  Journey,
  JourneyCollection,
  Shipment,
  TimeWindow,
  Waypoint,
} from 'interfaces/journey';
import { useSelector } from 'react-redux';
import { authProfile } from 'redux/auth/selectors';
import Api from 'util/Api';
import { isEmptyDraftJourney, usePrepRoutes } from './list';

interface BackendError {
  error?: string;
  details?: string;
  code?: string;
  state?: string;
  journeyId?: string;
  activeJourneyId?: string;
}

class JourneyError extends Error {
  readonly code?: string;
  readonly state?: string;
  readonly details?: string;
  readonly status?: number;
  readonly error?: string;
  readonly journeyId?: string;
  readonly activeJourneyId?: string;

  constructor(data: BackendError, status?: number) {
    super(data.error ?? data.details ?? 'Ocurrió un error inesperado.');
    this.name = 'JourneyError';
    this.code = data.code;
    this.state = data.state;
    this.details = data.details;
    this.status = status;
    this.error = data.error;
    this.journeyId = data.journeyId;
    this.activeJourneyId = data.activeJourneyId;
  }
}

const toJourneyError = (error: unknown): JourneyError => {
  if (error instanceof JourneyError) return error;
  const { response } = (error ?? {}) as AxiosError<BackendError>;
  return response
    ? new JourneyError(response.data ?? {}, response.status)
    : new JourneyError({});
};

export interface JourneyResponse extends Journey {
  inputResponse?: number;
  inputtedExternal?: string;
  inputCode?: string;
  details?: string;
  message?: string;
}

interface CreateResponse {
  journeyId?: string;
  details?: string;
}

interface ShipmentResponse {
  id: string;
  message?: string;
  externalCode: string;
  originAddress?: Waypoint;
  estimatedDistance?: number;
  estimatedTime?: number;
  waypoints?: Waypoint[];
  actionsWithoutWaypoints?: Action[];
  geometry?: string;
  shipments: Shipment[];
  error?: string;
  code?: string;
  issue?: Issue;
}

interface SetScheduleResponse {
  journeyId?: string;
  code?: string;
  message?: string;
}

export const ISSUE_CODES = new Set([
  'SuccessWithIssues',
  'ReturnAfterSchedule',
  'SuccessNotGeo',
]);

export interface OfferResponse {
  journeyId?: string;
  driverId?: number;
  status?: string;
  estimatedDistance?: number;
  estimatedTime?: number;
  message?: string;
}

const createJourney = async (
  distributionCenterId: number,
  kind: JourneyKind,
) => {
  try {
    const response = await Api.apiAxios.post<CreateResponse>(
      api.journeys.create,
      { distributionCenterId, kind },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const dismantleJourney = async (id: number | string) => {
  try {
    const response = await Api.apiAxios.put<JourneyResponse>(
      api.journeys.dismantle(id),
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const forceEndJourney = async (
  id: number | string,
  endReasonComment?: string,
) => {
  try {
    const payload = endReasonComment ? { endReasonComment } : {};
    const response = await Api.apiAxios.put<{
      journeyId: string;
      status: string;
      details: string;
    }>(api.journeys.forceEnd(id), payload);

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const addShipment = async (journeyId: string, shipmentId: number) => {
  try {
    const response = await Api.apiAxios.post<ShipmentResponse>(
      api.journeys.shipment(journeyId),
      { shipmentId },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const removeShipment = async (journeyId: string, shipmentId: number) => {
  try {
    const response = await Api.apiAxios.delete(
      api.journeys.remove(journeyId, shipmentId),
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

interface AuditLogResponse {
  logs: AuditLog[];
}

export const fetchAuditLog = async (journeyId?: string) => {
  try {
    if (!journeyId) return [];
    const response = await Api.apiAxios.get<
      AuditLogResponse & { message: string }
    >(api.journeys.getAuditLog(journeyId));

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data.logs.reverse();
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const fetchJourney = async (
  journeyId: string,
): Promise<Journey | undefined> => {
  try {
    const response = await Api.apiAxios.get<JourneyResponse>(
      api.journeys.get(journeyId),
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const redraftJourney = async (journeyId: string) => {
  try {
    const response = await Api.apiAxios.put(api.journeys.redraft(journeyId));

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const setJourneySchedule = async (
  journeyId: string,
  schedule: TimeWindow | null,
) => {
  try {
    const response = schedule
      ? await Api.apiAxios.put<SetScheduleResponse>(
          api.journeys.setSchedule(journeyId),
          schedule,
        )
      : await Api.apiAxios.delete<SetScheduleResponse>(
          api.journeys.setSchedule(journeyId),
        );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const unassignDriver = async (journeyId: string, driverId: number) => {
  try {
    const response = await Api.apiAxios.put(
      api.journeys.unassignDriver(journeyId),
      { driverId },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const unassignShipmentDriver = async (shipmentId: number) => {
  try {
    const response = await Api.apiAxios.put(api.paquer.unassign(shipmentId), {
      id: shipmentId,
    });

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const assignShipmentDriver = async (shipmentId: number, driverId: number) => {
  try {
    const response = await Api.apiAxios.put(
      api.paquer.assignShipment(shipmentId),
      { driverId },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const assignDriver = async (
  journeyId: string,
  driverId: number,
  vehiclePlate?: string,
) => {
  try {
    const response = await Api.apiAxios.put(
      api.journeys.assignDriver(journeyId),
      { driverId, vehiclePlate },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const offerJourney = async (id: string) => {
  try {
    const response = await Api.apiAxios.put(api.journeys.offer(id));

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const useJourney = (journeyId: string) => {
  return useQuery<Journey | undefined>({
    queryKey: ['journey', journeyId],
    queryFn: () => fetchJourney(journeyId),
    retry: 1,
    refetchInterval: 10000,
  });
};

export const useAuditLog = (journeyId?: string, enabled?: boolean) => {
  return useQuery<AuditLog[] | undefined>({
    queryKey: ['auditLog', journeyId],
    queryFn: () => fetchAuditLog(journeyId),
    retry: 1,
    enabled: enabled,
  });
};

export const useCreateJourney = () => {
  const queryClient = useQueryClient();
  const profile = useSelector(authProfile);
  return useMutation({
    mutationFn: (kind: JourneyKind) =>
      createJourney(profile?.paquerypoint.id, kind),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
      message.success('Se creó la ruta correctamente.');
    },
    onError: (error: JourneyError) =>
      notification.error({
        message: 'Ocurrio un error al crear la ruta',
        description: error.message,
      }),
  });
};

export const useGetOrCreateJourney = () => {
  const { data: prepRoutes } = usePrepRoutes();
  const { mutateAsync: createJourney, ...rest } = useCreateJourney();

  const getOrCreateJourney = async () => {
    const candidates = prepRoutes?.journeys?.filter(isEmptyDraftJourney) ?? [];
    const existing = candidates.reduce<Journey | undefined>(
      (oldest, journey) => {
        if (!oldest) return journey;
        const journeyDate = journey.creationDate
          ? new Date(journey.creationDate).getTime()
          : Infinity;
        const oldestDate = oldest.creationDate
          ? new Date(oldest.creationDate).getTime()
          : Infinity;
        return journeyDate < oldestDate ? journey : oldest;
      },
      undefined,
    );
    if (existing?.journeyId) {
      message.info('Se encontró una ruta vacía existente, redirigiendo...');
      return { journeyId: existing.journeyId };
    }
    return createJourney('shipments');
  };

  return { getOrCreateJourney, ...rest };
};

export const useDismantleJourney = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dismantleJourney,
    onError: (error: JourneyError) =>
      notification.error({
        message: 'Ocurrio un error al desarmar la ruta',
        description: error.message,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['trackingRoutes'],
      });
      message.success('Se desarmó la ruta con éxito.');
    },
  });
};

export const useForceEndJourney = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      endReasonComment,
    }: {
      id: number | string;
      endReasonComment?: string;
    }) => forceEndJourney(id, endReasonComment),
    onError: (error: JourneyError) =>
      notification.error({
        message: 'Ocurrio un error al terminar la ruta',
        description: error.message,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['trackingRoutes'],
      });
      message.success('Se terminó la ruta con éxito.');
    },
  });
};

export const useAddShipment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      shipmentId,
    }: {
      journeyId: string;
      shipmentId: number;
      externalCode: string;
    }) => addShipment(journeyId, shipmentId),
    onSuccess: (addResponse: ShipmentResponse, { journeyId, externalCode }) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          estimatedDistance:
            addResponse.estimatedDistance ?? draftJourney.estimatedDistance,
          originAddress:
            addResponse.originAddress ?? draftJourney.originAddress,
          estimatedTime:
            addResponse.estimatedTime ?? draftJourney.estimatedTime,
          waypoints: addResponse.waypoints ?? draftJourney.waypoints,
          actionsWithoutWaypoints:
            addResponse.actionsWithoutWaypoints ??
            draftJourney.actionsWithoutWaypoints,
          geometry: addResponse.geometry ?? draftJourney.geometry,
          shipments: addResponse.shipments ?? draftJourney.shipments,
          issue: addResponse.issue,
          message: addResponse.message,
          inputResponse: 200,
          inputtedExternal: externalCode,
        });
      }
      if (addResponse.code && ISSUE_CODES.has(addResponse.code)) {
        if (!addResponse.message && addResponse.code === 'SuccessNotGeo') {
          message.warning('El paquete no tiene geolocalización.');
        } else if (addResponse.message) {
          message.warning(addResponse.message);
        }
      }
      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          inputCode: addResponse.code,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['journey', journeyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
    },
    onError: (error: JourneyError, { journeyId, externalCode }) => {
      console.error(error);
      const { status } = error;
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          inputResponse: status,
          inputtedExternal: externalCode,
        });
      }
      if (error.code === 'ShipmentAlreadyInRoute' && error.journeyId) {
        const { journeyId: otherJourneyId } = error;
        const key = `shipment-already-in-route-${otherJourneyId}`;
        return notification.error({
          placement: 'top',
          key,
          message: `${error.message} (${otherJourneyId})`,
          btn: createElement(
            Button,
            {
              type: 'primary',
              size: 'small',
              onClick: () => {
                window.open(`/journey/${otherJourneyId}`, '_blank');
                notification.destroy(key);
              },
            },
            'Ver ruta',
          ),
        });
      }
      message.error(error.message);
    },
  });
};

export const useRemoveShipment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      shipmentId,
    }: {
      journeyId: string;
      shipmentId: number;
      externalCode: string;
    }) => removeShipment(journeyId, shipmentId),
    onSuccess: (addResponse: ShipmentResponse, { journeyId, externalCode }) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          estimatedDistance:
            addResponse.estimatedDistance ?? draftJourney.estimatedDistance,
          originAddress:
            addResponse.originAddress ?? draftJourney.originAddress,
          estimatedTime:
            addResponse.estimatedTime ?? draftJourney.estimatedTime,
          waypoints: addResponse.waypoints ?? draftJourney.waypoints,
          actionsWithoutWaypoints:
            addResponse.actionsWithoutWaypoints ??
            draftJourney.actionsWithoutWaypoints,
          geometry: addResponse.geometry ?? draftJourney.geometry,
          shipments: addResponse.shipments ?? draftJourney.shipments,
          issue: addResponse.issue,
          message: 'delete',
          inputResponse: 200,
          inputtedExternal: externalCode,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['journey', journeyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
    },
    onError: (error: JourneyError) => {
      notification.error({
        message: 'Ocurrió un error al remover el paquete',
        description: error.message,
      });
    },
  });
};

// Rechazos del unassign que traen la ruta en conflicto en `journeyId`: el operador tiene que
// poder ir a verla para resolver, así que la notificación lleva el botón "Ver ruta".
const CONFLICT_JOURNEY_CODES = [
  'ShipmentInStartedJourney',
  'ShipmentInTransferingJourney',
];

// Desasigna el driver de un envío puntual (ruta finalizada)
export const useUnassignShipmentDriver = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId }: { journeyId?: string; shipmentId: number }) =>
      unassignShipmentDriver(shipmentId),
    onSuccess: (_response, { journeyId }) => {
      if (journeyId)
        queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
      queryClient.invalidateQueries({ queryKey: ['driverPackages'] });
      message.success('Se desasignó el driver del envío correctamente.');
    },
    onError: (error: JourneyError) => {
      if (
        error.code &&
        CONFLICT_JOURNEY_CODES.includes(error.code) &&
        error.journeyId
      ) {
        const { journeyId: conflictJourneyId } = error;
        const key = `shipment-in-journey-${conflictJourneyId}`;
        return notification.error({
          key,
          message: 'Ocurrió un error al desasignar el driver del envío',
          description: `${error.message} (${conflictJourneyId})`,
          btn: createElement(
            Button,
            {
              type: 'primary',
              size: 'small',
              onClick: () => {
                window.open(`/journey/${conflictJourneyId}`, '_blank');
                notification.destroy(key);
              },
            },
            'Ver ruta',
          ),
        });
      }
      notification.error({
        message: 'Ocurrió un error al desasignar el driver del envío',
        description: error.message,
      });
    },
  });
};

// Asigna un envío a un driver. El backend rechaza con 409 si el paquete ya está
// planificado en la ruta de otro driver; si está en una ruta sin driver, lo saca
// de ahí y asigna.
export const useAssignShipmentDriver = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      driverId,
    }: {
      shipmentId: number;
      driverId: number;
    }) => assignShipmentDriver(shipmentId, driverId),
    onSuccess: () => {
      // pudo haber salido de una ruta en preparación
      queryClient.invalidateQueries({ queryKey: ['prepRoutes'] });
    },
    onError: (error: JourneyError) => {
      if (error.code === 'ShipmentAlreadyInRoute' && error.journeyId) {
        const { journeyId: otherJourneyId } = error;
        const key = `shipment-already-in-route-${otherJourneyId}`;
        return notification.error({
          key,
          message: 'No se pudo asignar el envío',
          description: `${error.message} (${otherJourneyId})`,
          btn: createElement(
            Button,
            {
              type: 'primary',
              size: 'small',
              onClick: () => {
                window.open(`/journey/${otherJourneyId}`, '_blank');
                notification.destroy(key);
              },
            },
            'Ver ruta',
          ),
        });
      }
      return notification.error({
        message: 'No se pudo asignar el envío',
        description: error.message,
      });
    },
  });
};

// Oferta la ruta, no importa si tiene o no driver
export const useOffer = () => {
  const queryClient = useQueryClient();
  return useMutation<OfferResponse, JourneyError, string>({
    mutationFn: async (id: string) =>
      offerJourney(id) as Promise<OfferResponse>,
    onSuccess: (response, id) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        id,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', id], {
          ...draftJourney,
          state: response.status,
        });
      }
      if (draftJourney?.driver?.id)
        message.success('Se ofreció la ruta al driver correctamente.');
      else message.success('Se ofertó la ruta correctamente.');

      queryClient.invalidateQueries({
        queryKey: ['journey', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['trackingRoutes'],
      });
    },
    onError: (error: JourneyError, id) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        id,
      ]);
      const label = draftJourney?.driver?.id ? 'ofrecer' : 'ofertar';
      notification.error({
        message: `Ocurrió un error al ${label} la ruta`,
        description: error.message,
      });
    },
  });
};

// Desasigna y desoferta la ruta, vuelve a preparación
export const useRedraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (journeyId: string) => redraftJourney(journeyId),
    onSuccess: (response, id) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        id,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', id], {
          ...draftJourney,
          state: response.status,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['journey', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['trackingRoutes'],
      });
      message.success('Se desofertó la ruta correctamente.');
    },
    onError: (error: JourneyError) =>
      notification.error({
        message: 'Ocurrió un error al volver la ruta a preparación',
        description: error.message,
      }),
  });
};

interface AssignDriverProps {
  journeyId: string;
  driverId: number;
  vehiclePlate?: string;
}

const setJourneyName = async (journeyId: string, name: string) => {
  try {
    const response = await Api.apiAxios.put(api.journeys.name(journeyId), {
      name,
    });
    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }
    return response.data as { name?: string };
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const useSetJourneyName = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ journeyId, name }: { journeyId: string; name: string }) =>
      setJourneyName(journeyId, name),
    onSuccess: (response, { journeyId }) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          name: response.name ?? '',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
      queryClient.invalidateQueries({ queryKey: ['prepRoutes'] });
      message.success('Se guardó el nombre de la ruta.');
    },
    onError: (error: JourneyError) => message.error(error.message),
  });
};

const setJourneyKind = async (journeyId: string, kind: JourneyKind) => {
  try {
    const response = await Api.apiAxios.put(api.journeys.kind(journeyId), {
      kind,
    });
    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }
    return response.data as { kind?: JourneyKind };
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

// El backend rechaza con 409 si la ruta lleva algo que el tipo nuevo no acepta
// (colectas al pasar a envíos, paquetes al pasar a colectas); el mensaje dice qué sacar.
export const useSetJourneyKind = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      kind,
    }: {
      journeyId: string;
      kind: JourneyKind;
    }) => setJourneyKind(journeyId, kind),
    onSuccess: (response, { journeyId }) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          kind: response.kind ?? draftJourney.kind,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
      queryClient.invalidateQueries({ queryKey: ['prepRoutes'] });
      message.success('Se cambió el tipo de la ruta.');
    },
    onError: (error: JourneyError) => message.error(error.message),
  });
};

export const getDriverVehiclePlates = async (
  driverId: number,
): Promise<string[]> => {
  const response = await Api.apiAxios.get(api.journeys.vehiclePlates, {
    params: { driverId },
  });
  return Api.isSuccessResponse(response) ? (response.data?.plates ?? []) : [];
};

export const useAssignDriver = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ journeyId, driverId, vehiclePlate }: AssignDriverProps) =>
      assignDriver(journeyId, driverId, vehiclePlate),
    onSuccess: (response, { journeyId, driverId }) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          driver: { id: driverId },
          estimatedDistance:
            response.estimatedDistance ?? draftJourney.estimatedDistance,
          originAddress: response.originAddress ?? draftJourney.originAddress,
          estimatedTime: response.estimatedTime ?? draftJourney.estimatedTime,
          waypoints: response.waypoints ?? draftJourney.waypoints,
          actionsWithoutWaypoints:
            response.actionsWithoutWaypoints ??
            draftJourney.actionsWithoutWaypoints,
          geometry: response.geometry ?? draftJourney.geometry,
          shipments: response.shipments ?? draftJourney.shipments,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['journey', journeyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['trackingRoutes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
      message.success('Se asignó el driver correctamente');
    },
    onError: (error: JourneyError) => {
      const conflictJourneyId =
        (error.code === 'DriverShipmentsInOtherRoute' && error.journeyId) ||
        (error.code === 'DriverAlreadyOnActiveJourney' &&
          error.activeJourneyId);
      if (conflictJourneyId) {
        const key = `driver-shipments-in-route-${conflictJourneyId}`;
        return notification.error({
          key,
          message: 'No se pudo asignar el driver',
          description: error.message,
          btn: createElement(
            Button,
            {
              type: 'primary',
              size: 'small',
              onClick: () => {
                window.open(`/journey/${conflictJourneyId}`, '_blank');
                notification.destroy(key);
              },
            },
            'Ver ruta',
          ),
        });
      }
      return message.error(error.message);
    },
  });
};

export const useUnassign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ journeyId, driverId }: AssignDriverProps) =>
      unassignDriver(journeyId, driverId),
    onSuccess: (response, { journeyId }) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);

      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          estimatedDistance:
            response.estimatedDistance ?? draftJourney.estimatedDistance,
          originAddress: response.originAddress ?? draftJourney.originAddress,
          estimatedTime: response.estimatedTime ?? draftJourney.estimatedTime,
          waypoints: response.waypoints ?? draftJourney.waypoints,
          actionsWithoutWaypoints:
            response.actionsWithoutWaypoints ??
            draftJourney.actionsWithoutWaypoints,
          geometry: response.geometry ?? draftJourney.geometry,
          shipments: response.shipments ?? draftJourney.shipments,
          driver: null,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['journey', journeyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['trackingRoutes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
      message.success('Se desasignó el driver correctamente');
    },
    onError: (error: JourneyError) =>
      notification.error({
        message: 'Ocurrio un error al desasignar al driver',
        description: error.message,
      }),
  });
};

interface SetOriginProps {
  journeyId: string;
  geolocationType: GeolocationType;
  address?: string;
}

const setOrigin = async (
  journeyId: string,
  geolocationType: GeolocationType,
  address?: string,
  distributionCenterId?: number,
) => {
  try {
    const response = await Api.apiAxios.put(
      api.journeys.setOrigin(journeyId),
      geolocationType === GeolocationType.DistributionCenter
        ? {
            geolocationType,
            distributionCenterId,
          }
        : {
            geolocationType,
            address,
          },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const useSetJourneySchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      schedule,
    }: {
      journeyId: string;
      schedule: TimeWindow | null;
    }) => setJourneySchedule(journeyId, schedule),
    onSuccess: (response, { journeyId, schedule }) => {
      if (response.code && ISSUE_CODES.has(response.code)) {
        if (response.message) message.warning(response.message);
      } else if (schedule) {
        message.success('Se asignó el rango horario sin inconvenientes.');
      } else {
        message.success('Se quitó el rango horario exitosamente.');
      }
      queryClient.invalidateQueries({
        queryKey: ['journey', journeyId],
      });
    },
    onError: (error: JourneyError) => {
      notification.error({
        message: 'Ocurrió un error al establecer el horario',
        description: error.message,
      });
    },
  });
};

export const useSetOrigin = () => {
  const queryClient = useQueryClient();
  const profile = useSelector(authProfile);
  return useMutation({
    mutationFn: ({ journeyId, geolocationType, address }: SetOriginProps) =>
      setOrigin(journeyId, geolocationType, address, profile?.paquerypoint.id),
    onSuccess: (response, { journeyId }) => {
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);
      if (draftJourney) {
        queryClient.setQueryData(['journey', journeyId], {
          ...draftJourney,
          originAddress: response.originAddress ?? draftJourney.originAddress,
          estimatedDistance:
            response.estimatedDistance ?? draftJourney.estimatedDistance,
          estimatedTime: response.estimatedTime ?? draftJourney.estimatedTime,
          geometry: response.geometry ?? draftJourney.geometry,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['journey', journeyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['trackingRoutes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['prepRoutes'],
      });
      message.success('Se configuró el origen correctamente');
    },
    onError: (error: JourneyError) => {
      console.error(error);
      return message.error(error.message);
    },
  });
};

enum JoinFailureCode {
  DriverAssigned = 'JourneyHasDriverAssigned',
  CantProcess = 'CantProcess',
  DbError = 'DatabaseError',
  TemporalError = 'TemporalError',
}

interface JoinJourneyResponse {
  failed: [
    {
      code: JoinFailureCode;
      journeyId: string;
      reason: string;
    },
  ];
  successful: [
    {
      journeyId: string;
    },
  ];
  totalAttempted: number;
  totalFailed: number;
  totalSuccessful: number;
  code: string;
  details: string;
  error: string;
  state: string;
}

const joinJourneys = async (journeyId: string, journeys: string[]) => {
  try {
    const response = await Api.apiAxios.put<JoinJourneyResponse>(
      api.journeys.join(journeyId),
      {
        journeyIds: journeys,
      },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const transferJourney = async (journeyId: string, targetJourneyId: string) => {
  try {
    const response = await Api.apiAxios.put(api.journeys.transfer(journeyId), {
      targetJourneyId,
    });

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const useTransferJourney = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      targetJourneyId,
    }: {
      journeyId: string;
      targetJourneyId: string;
    }) => transferJourney(journeyId, targetJourneyId),
    onSuccess: (_, { journeyId }) => {
      queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
      queryClient.invalidateQueries({ queryKey: ['prepRoutes'] });
      queryClient.invalidateQueries({ queryKey: ['trackingRoutes'] });
      message.success('Se inició la transferencia correctamente.');
    },
    onError: (error: JourneyError) =>
      notification.error({
        message: 'Ocurrió un error al transferir la ruta',
        description: error.message,
      }),
  });
};

interface TransferByOperatorParams {
  journeyId: string;
  address: string;
  geolocationType: string;
  newDriverId: number;
}

const transferJourneyByOperator = async ({
  journeyId,
  address,
  geolocationType,
  newDriverId,
}: TransferByOperatorParams) => {
  try {
    const response = await Api.apiAxios.put(
      api.journeys.transferByOperator(journeyId),
      { address, geolocationType, newDriverId },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const useTransferJourneyByOperator = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: TransferByOperatorParams) =>
      transferJourneyByOperator(params),
    onSuccess: (_, { journeyId }) => {
      queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
      queryClient.invalidateQueries({ queryKey: ['trackingRoutes'] });
      message.success('Se inició la transferencia correctamente.');
    },
    onError: (error: JourneyError) => {
      if (error.status === 400) {
        notification.warning({
          message: 'No se pudo asignar la transferencia',
          description: error.error,
        });
      } else {
        notification.error({
          message: 'Ocurrió un error al transferir la ruta',
          description: error.message,
        });
      }
    },
  });
};

const cancelTransferByOperator = async (journeyId: string) => {
  try {
    const response = await Api.apiAxios.put(
      api.journeys.cancelTransfer(journeyId),
    );

    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const useCancelTransferByOperator = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (journeyId: string) => cancelTransferByOperator(journeyId),
    onSuccess: (_, journeyId) => {
      queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
      queryClient.invalidateQueries({ queryKey: ['prepRoutes'] });
      queryClient.invalidateQueries({ queryKey: ['trackingRoutes'] });
      message.success('Se canceló la transferencia correctamente.');
    },
    onError: (error: JourneyError) =>
      notification.error({
        message: 'Ocurrió un error al cancelar la transferencia',
        description: error.message,
      }),
  });
};

interface UseFusionProps {
  journeyId: string;
  journeys: string[];
}

export const useFusion = () => {
  return useMutation({
    mutationFn: async ({ journeyId, journeys }: UseFusionProps) =>
      joinJourneys(journeyId, journeys),
    onSuccess: (response) => {
      if (response.totalFailed > 0) {
        response.failed.forEach((fail) => {
          notification.error({
            message: `No se pudo unir la ruta ${fail.journeyId}`,
            description: fail.reason ?? 'Ocurrió un error.',
          });
        });
      } else {
        message.success('Se unieron todas las rutas correctamente.');
      }

      return response;
    },
    onError: (error: JourneyError) =>
      notification.error({
        message: 'Ocurrió un error al unir las rutas',
        description: error.message,
      }),
  });
};

interface CollectionInJourneyResponse extends ShipmentResponse {
  collections?: JourneyCollection[];
  collectionWaitMinutes?: number;
  totalCollections?: number;
  collectedCollections?: number;
  failedCollections?: number;
  pendingCollections?: number;
}

const addCollection = async (journeyId: string, collectionId: number) => {
  try {
    const response = await Api.apiAxios.post<CollectionInJourneyResponse>(
      api.journeys.collections(journeyId),
      { collectionId },
    );
    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }
    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const removeCollection = async (journeyId: string, collectionId: number) => {
  try {
    const response = await Api.apiAxios.delete(
      api.journeys.collection(journeyId, collectionId),
    );
    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }
    return response.data as CollectionInJourneyResponse;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

const releaseCollectionWait = async (
  journeyId: string,
  collectionId: number,
) => {
  try {
    const response = await Api.apiAxios.put(
      api.journeys.releaseCollectionWait(journeyId, collectionId),
    );
    if (!Api.isSuccessResponse(response)) {
      throw new JourneyError(response.data as BackendError, response.status);
    }
    return response.data;
  } catch (error) {
    console.error(error);
    throw toJourneyError(error);
  }
};

export const useReleaseCollectionWait = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      collectionId,
    }: {
      journeyId: string;
      collectionId: number;
    }) => releaseCollectionWait(journeyId, collectionId),
    onSuccess: (_response, { journeyId }) => {
      queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
      message.success(
        'Se liberó la espera: la parada quedó cerrada y el driver puede seguir.',
      );
    },
    onError: (error: JourneyError) => message.error(error.message),
  });
};

const writeCollectionResult = (
  queryClient: ReturnType<typeof useQueryClient>,
  journeyId: string,
  response: CollectionInJourneyResponse,
) => {
  const draftJourney = queryClient.getQueryData<JourneyResponse>([
    'journey',
    journeyId,
  ]);
  if (draftJourney) {
    queryClient.setQueryData(['journey', journeyId], {
      ...draftJourney,
      estimatedDistance:
        response.estimatedDistance ?? draftJourney.estimatedDistance,
      estimatedTime: response.estimatedTime ?? draftJourney.estimatedTime,
      originAddress: response.originAddress ?? draftJourney.originAddress,
      waypoints: response.waypoints ?? draftJourney.waypoints,
      actionsWithoutWaypoints:
        response.actionsWithoutWaypoints ??
        draftJourney.actionsWithoutWaypoints,
      geometry: response.geometry ?? draftJourney.geometry,
      collections: response.collections ?? draftJourney.collections,
      collectionWaitMinutes:
        response.collectionWaitMinutes ?? draftJourney.collectionWaitMinutes,
      totalCollections:
        response.totalCollections ?? draftJourney.totalCollections,
      collectedCollections:
        response.collectedCollections ?? draftJourney.collectedCollections,
      failedCollections:
        response.failedCollections ?? draftJourney.failedCollections,
      pendingCollections:
        response.pendingCollections ?? draftJourney.pendingCollections,
      issue: response.issue,
      message: response.message,
    });
  }
  queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
  queryClient.invalidateQueries({ queryKey: ['prepRoutes'] });
  queryClient.invalidateQueries({
    queryKey: ['collections-v2', 'collections'],
  });
};

export const useAddCollection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      collectionId,
    }: {
      journeyId: string;
      collectionId: number;
      storeName?: string;
    }) => addCollection(journeyId, collectionId),
    onSuccess: (response: CollectionInJourneyResponse, { journeyId }) => {
      writeCollectionResult(queryClient, journeyId, response);
      if (response.code && ISSUE_CODES.has(response.code) && response.message) {
        message.warning(response.message);
      }
    },
    onError: (error: JourneyError) => {
      if (error.code === 'CollectionAlreadyInRoute' && error.journeyId) {
        const { journeyId: otherJourneyId } = error;
        const key = `collection-already-in-route-${otherJourneyId}`;
        return notification.error({
          placement: 'top',
          key,
          message: `${error.message} (${otherJourneyId})`,
          btn: createElement(
            Button,
            {
              type: 'primary',
              size: 'small',
              onClick: () => {
                window.open(`/journey/${otherJourneyId}`, '_blank');
                notification.destroy(key);
              },
            },
            'Ver ruta',
          ),
        });
      }
      return message.error(error.message);
    },
  });
};

export const useRemoveCollection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      collectionId,
    }: {
      journeyId: string;
      collectionId: number;
      storeName?: string;
    }) => removeCollection(journeyId, collectionId),
    onSuccess: (
      response: CollectionInJourneyResponse,
      { journeyId, storeName },
    ) => {
      writeCollectionResult(queryClient, journeyId, response);
      message.success(
        storeName
          ? `Se quitó la colecta de ${storeName} de la ruta.`
          : 'Se quitó la colecta de la ruta.',
      );
    },
    onError: (error: JourneyError) => message.error(error.message),
  });
};
